/**
 * Edit gift matching pool page
 *
 * Shows a pool's linked campaigns, adds and removes campaigns, and saves the settings that every
 * linked gift match shares.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Message, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { systemSettingsRoutes } from '../constants';
import {
  addGiftMatchingPoolCampaigns,
  getGiftMatchingPool,
  getPoolCampaignSummary,
  removeGiftMatchingPoolCampaign,
  updateGiftMatchingPool
} from './api';
import { i18nKeys } from './constants';
import {
  accountLabel,
  conditionErrors,
  emptyPoolFormValues,
  errorMessageKeys,
  formatPoolDate,
  isCurrencyMismatch,
  isMultiOrganisation,
  toPoolFormValues,
  toPoolSettings
} from './helpers';
import {
  CampaignSummary,
  GiftMatchingPool,
  PoolCampaign,
  PoolFormValues
} from './types';
import CampaignPicker from './components/CampaignPicker';
import MultiOrganisationWarning from './components/MultiOrganisationWarning';
import PoolCampaignsTable from './components/PoolCampaignsTable';
import PoolSettingsFields from './components/PoolSettingsFields';
import RemoveCampaignModal from './components/RemoveCampaignModal';
import TargetCampaignsList from './components/TargetCampaignsList';
import Styles from './GiftMatchingPools.module.css';

const ErrorList: React.FC<{ keys: string[] }> = ({ keys }) =>
  keys.length > 0 ? (
    <Message negative role="alert">
      {keys.map((key) => (
        <p key={key}>{I18n.t(key)}</p>
      ))}
    </Message>
  ) : null;

const GiftMatchingPoolEdit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const poolId = Number(id);
  const methods = useForm<PoolFormValues>({
    defaultValues: emptyPoolFormValues(),
    mode: 'onBlur'
  });

  const [pool, setPool] = useState<GiftMatchingPool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [successKey, setSuccessKey] = useState<string | null>(
    (location.state as { created?: boolean } | null)?.created
      ? i18nKeys.EDIT.CREATED
      : null
  );
  const [saveErrorKeys, setSaveErrorKeys] = useState<string[]>([]);
  const [conditionErrorKeys, setConditionErrorKeys] = useState<string[]>([]);
  const [pending, setPending] = useState<CampaignSummary[]>([]);
  const [addErrorKeys, setAddErrorKeys] = useState<string[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [removing, setRemoving] = useState<PoolCampaign | null>(null);
  const [removeErrorKeys, setRemoveErrorKeys] = useState<string[]>([]);
  const [isRemoving, setIsRemoving] = useState(false);

  const showPool = useCallback(
    (loaded: GiftMatchingPool) => {
      setPool(loaded);
      methods.reset(toPoolFormValues(loaded));
    },
    [methods]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      showPool(await getGiftMatchingPool(poolId));
    } catch {
      setLoadError(I18n.t(i18nKeys.ERRORS.LOAD_FAILED));
    } finally {
      setIsLoading(false);
    }
  }, [poolId, showPool]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = methods.handleSubmit(
    async (values) => {
      setSuccessKey(null);
      setSaveErrorKeys([]);
      const invalidConditions = conditionErrors(values.conditions);
      setConditionErrorKeys(invalidConditions);
      if (!pool || invalidConditions.length > 0) {
        return;
      }
      try {
        showPool(await updateGiftMatchingPool(pool.id, toPoolSettings(values)));
        setSuccessKey(i18nKeys.EDIT.SAVED);
      } catch (error) {
        setSaveErrorKeys(errorMessageKeys(error, i18nKeys.ERRORS.SAVE_FAILED));
      }
    },
    () => setSaveErrorKeys([i18nKeys.ERRORS.NAME_REQUIRED])
  );

  const addPending = async (campaignId: number) => {
    setAddErrorKeys([]);
    const alreadyLinked = pool?.campaigns.some(
      (c) => c.campaignId === campaignId
    );
    if (alreadyLinked) {
      setAddErrorKeys([i18nKeys.ERRORS.CAMPAIGN_ALREADY_LINKED]);
      return;
    }
    if (pending.some((p) => p.campaignId === campaignId)) {
      setAddErrorKeys([i18nKeys.WARNINGS.DUPLICATE]);
      return;
    }
    try {
      const summary = await getPoolCampaignSummary(campaignId);
      setPending((current) =>
        current.some((p) => p.campaignId === summary.campaignId)
          ? current
          : [...current, summary]
      );
    } catch {
      setAddErrorKeys([i18nKeys.ERRORS.SUMMARY_FAILED]);
    }
  };

  const onAddCampaigns = async () => {
    if (!pool || pending.length === 0) {
      return;
    }
    if (pending.some((p) => isCurrencyMismatch(p.currency, pool.currency))) {
      setAddErrorKeys([i18nKeys.ERRORS.CURRENCY]);
      return;
    }
    setIsAdding(true);
    setAddErrorKeys([]);
    setSuccessKey(null);
    try {
      const updated = await addGiftMatchingPoolCampaigns(
        pool.id,
        pending.map((p) => p.campaignId)
      );
      setPool(updated);
      setPending([]);
      setSuccessKey(i18nKeys.EDIT.ADDED);
    } catch (error) {
      setAddErrorKeys(errorMessageKeys(error, i18nKeys.ERRORS.ADD_FAILED));
    } finally {
      setIsAdding(false);
    }
  };

  const onConfirmRemove = async (keepGiftMatching: boolean) => {
    if (!pool || !removing) {
      return;
    }
    setIsRemoving(true);
    setRemoveErrorKeys([]);
    setSuccessKey(null);
    try {
      await removeGiftMatchingPoolCampaign(
        pool.id,
        removing.giftMatchingId,
        keepGiftMatching
      );
    } catch (error) {
      setRemoveErrorKeys(
        errorMessageKeys(error, i18nKeys.ERRORS.REMOVE_FAILED)
      );
      setIsRemoving(false);
      return;
    }
    setIsRemoving(false);
    setRemoving(null);
    setSuccessKey(i18nKeys.EDIT.REMOVED);
    // The removal stands even if reloading the pool fails.
    try {
      setPool(await getGiftMatchingPool(pool.id));
    } catch {
      setLoadError(I18n.t(i18nKeys.ERRORS.LOAD_FAILED));
    }
  };

  const organisationIds = [
    ...(pool?.campaigns.map((c) => c.organizationId) ?? []),
    ...pending.map((p) => p.organizationId)
  ];
  const poolOrganisation = pool?.campaigns[0];

  return (
    <div>
      <div className={Styles.header}>
        <h2>
          <Translate value={i18nKeys.EDIT.TITLE} />
          {pool && `: ${pool.name}`}
        </h2>
        <Button
          basic
          type="button"
          onClick={() => navigate(systemSettingsRoutes.GIFT_MATCHING_POOLS)}
        >
          <Translate value={i18nKeys.BACK} />
        </Button>
      </div>

      {loadError && <Message negative role="alert" content={loadError} />}
      {successKey && <Message positive content={I18n.t(successKey)} />}

      {!isLoading && pool && (
        <>
          <p className={Styles.audit}>
            {I18n.t(i18nKeys.EDIT.AUDIT, {
              createdBy:
                accountLabel(pool.createdBy) ??
                I18n.t(i18nKeys.EDIT.UNKNOWN_ACCOUNT),
              createdAt: formatPoolDate(pool.createdAt),
              updatedBy:
                accountLabel(pool.lastUpdatedBy) ??
                I18n.t(i18nKeys.EDIT.UNKNOWN_ACCOUNT),
              updatedAt: formatPoolDate(pool.lastUpdatedAt)
            })}
          </p>

          <Segment className={Styles.section}>
            <h3>
              <Translate value={i18nKeys.EDIT.CAMPAIGNS_TITLE} />
            </h3>
            {isMultiOrganisation(organisationIds) && (
              <MultiOrganisationWarning />
            )}
            <PoolCampaignsTable
              campaigns={pool.campaigns}
              onRemove={(campaign) => {
                setRemoveErrorKeys([]);
                setRemoving(campaign);
              }}
            />

            <h4>
              <Translate value={i18nKeys.EDIT.ADD_TITLE} />
            </h4>
            <Form>
              <CampaignPicker
                idPrefix="pool-add"
                label={I18n.t(i18nKeys.PICKER.SEARCH)}
                organizationId={poolOrganisation?.organizationId}
                organizationName={poolOrganisation?.organizationName}
                disabled={isAdding}
                onPick={addPending}
              />
            </Form>
            <TargetCampaignsList
              campaigns={pending}
              poolCurrency={pool.currency}
              onRemove={(campaignId) =>
                setPending((current) =>
                  current.filter((p) => p.campaignId !== campaignId)
                )
              }
            />
            <ErrorList keys={addErrorKeys} />
            {pending.length > 0 && (
              <Button
                type="button"
                loading={isAdding}
                disabled={isAdding}
                onClick={onAddCampaigns}
              >
                <Translate value={i18nKeys.EDIT.ADD_BUTTON} />
              </Button>
            )}
          </Segment>

          <Segment className={Styles.section}>
            <h3>
              <Translate value={i18nKeys.EDIT.SETTINGS_TITLE} />
            </h3>
            <FormProvider {...methods}>
              <Form onSubmit={onSave} noValidate>
                <PoolSettingsFields
                  idPrefix="pool-edit"
                  conditionErrorKeys={conditionErrorKeys}
                />
                <ErrorList keys={saveErrorKeys} />
                <div className={Styles.actions}>
                  <Button
                    primary
                    type="submit"
                    loading={methods.formState.isSubmitting}
                    disabled={methods.formState.isSubmitting}
                  >
                    <Translate value={i18nKeys.EDIT.SAVE} />
                  </Button>
                </div>
              </Form>
            </FormProvider>
          </Segment>

          <RemoveCampaignModal
            campaign={removing}
            errorKeys={removeErrorKeys}
            isRemoving={isRemoving}
            onCancel={() => setRemoving(null)}
            onConfirm={onConfirmRemove}
          />
        </>
      )}
    </div>
  );
};

export default GiftMatchingPoolEdit;
